import {
  Anchor,
  Button,
  ColorSwatch,
  Group,
  Image,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link, useParams } from "@tanstack/react-router";
import { Pencil, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetBrand } from "../../api/generated/endpoints/teams/teams";
import { freshUrls, soonest } from "../../api/signed-urls";
import type { Brand } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useMembership } from "../../platform/membership";
import { managesBrands } from "../../platform/permissions";
import { CentredPage, type BackLink } from "../../ui/CentredPage";
import { DeleteBrand } from "../../platform/brands/DeleteBrand";
import { TYPOGRAPHY_POLL_MS, awaitingTypography } from "./typography-wait";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Table.Tr>
      <Table.Th w={180} scope="row" style={{ verticalAlign: "top" }}>
        {label}
      </Table.Th>
      <Table.Td style={{ whiteSpace: "pre-wrap" }}>{children ?? "Not set"}</Table.Td>
    </Table.Tr>
  );
}

/** A brand's page on its own route: the back arrow to the brands, Edit to its own route. */
export function BrandScreen() {
  const { teamId, brandId } = useParams({ from: "/signed-in/teams/$teamId/brands/$brandId/" });
  return (
    <BrandPage
      teamId={teamId}
      brandId={brandId}
      back={(props) => <Link {...props} to="/teams/$teamId/brands" params={{ teamId }} />}
      edit={(props) => (
        <Link {...props} to="/teams/$teamId/brands/$brandId/edit" params={{ teamId, brandId }} />
      )}
    />
  );
}

export interface BrandPageProps {
  teamId: string;
  brandId: string;
  /** The back arrow's link. */
  back: BackLink;
  /** "Edit brand"'s link, as `renderRoot` takes it. */
  edit: BackLink;
  /** Where the page goes with the brand deleted; the team's Brands page when not given. */
  onDeleted?: (() => Promise<void>) | undefined;
}

/**
 * The brand as the API returns it, in the live app's centered page with the back arrow, wherever
 * it is drawn (Classic's route, or Pro's, spec ui-perf/ui-switch-classic-simple-pro PR e4). AI
 * output is shown, never edited (A3 decision 6).
 */
export function BrandPage({ teamId, brandId, back, edit, onDeleted }: BrandPageProps) {
  const me = useMembership(teamId);
  // The logo's signed URL expires: the brand is read again before it does (#123). While the
  // worker classifies its first font, it is read every few seconds for a minute (spec G15b).
  const [openedAt] = useState(() => Date.now());
  const fresh = freshUrls((read: Brand) => read.urls_expire_at);
  const brand = useGetBrand(teamId, brandId, {
    query: {
      ...fresh,
      refetchInterval: (query) =>
        soonest(
          fresh.refetchInterval(query),
          awaitingTypography(query.state.data, openedAt) ? TYPOGRAPHY_POLL_MS : false,
        ),
    },
  });
  const [deleting, setDeleting] = useState(false);
  if (brand.isPending) return <Loader size="sm" aria-label="Loading the brand" />;
  if (brand.isError) return <ErrorAlert error={brand.error} />;
  const { data } = brand;
  const canManage = me !== undefined && managesBrands(me.role);
  const actions = canManage ? (
    <Group gap="xs">
      <Button
        variant="outline"
        leftSection={<Pencil size={16} />}
        renderRoot={edit}
      >
        Edit brand
      </Button>
      <Button
        variant="subtle"
        color="red"
        leftSection={<Trash2 size={16} />}
        onClick={() => setDeleting(true)}
      >
        Delete brand
      </Button>
    </Group>
  ) : null;
  return (
    <CentredPage
      title={data.name}
      back={back}
      right={actions}
    >
      <Stack gap="lg" pb="md">
        {data.logo_url === null ? null : (
          <Image src={data.logo_url} alt={`${data.name} logo`} maw={200} mah={80} fit="contain" />
        )}
        <Table>
          <Table.Tbody>
            <Field label="Description">{data.description}</Field>
            <Field label="Website">
              {data.website === null ? null : (
                <Anchor href={data.website} target="_blank" rel="noopener noreferrer">
                  {data.website}
                </Anchor>
              )}
            </Field>
            <Field label="Colors">
              {data.colors.length === 0 ? null : (
                <Group gap="sm">
                  {data.colors.map((color) => (
                    <Group key={color.hex} gap={4}>
                      <ColorSwatch color={color.hex} size={16} radius="xs" aria-hidden />
                      <Text size="sm">{color.label ?? color.hex}</Text>
                    </Group>
                  ))}
                </Group>
              )}
            </Field>
            <Field label="Fonts">
              {data.fonts.length === 0
                ? null
                : data.fonts.map((font) => `${font.name} (${font.source})`).join(", ")}
            </Field>
            <Field label="Tone of voice">{data.tone_of_voice}</Field>
            <Field label="Guidelines">{data.guidelines}</Field>
            <Field label="Audience">{data.audience}</Field>
          </Table.Tbody>
        </Table>
        <WrittenByAi brand={data} />
      </Stack>
      {deleting ? (
        <DeleteBrand
          teamId={teamId}
          brand={data}
          onCancel={() => setDeleting(false)}
          onDeleted={onDeleted}
        />
      ) : null}
    </CentredPage>
  );
}

/** `typography` and `brand_query`, which the extraction jobs write (A3, Out). */
function WrittenByAi({ brand }: { brand: Brand }) {
  const typography = brand.typography;
  return (
    <Stack gap="xs">
      <Title order={2} size="h4">
        Written by AI
      </Title>
      <Table>
        <Table.Tbody>
          <Field label="Typography">
            {typography === null
              ? "Not generated yet"
              : [
                  typography.typeface,
                  typography.font_style,
                  typography.case_treatment,
                  typography.typography_details,
                ]
                  .filter((part) => part !== null && part !== undefined && part !== "")
                  .join(". ")}
          </Field>
          <Field label="Brand query">{brand.brand_query ?? "Not generated yet"}</Field>
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
